// WP-3.1 deploy-prep (OPS-4 / OPS-2) — Google Tag Manager container + Consent Mode v2.
//
// GTM is the SINGLE governed tag container (OPS-4): the app loads ONLY GTM; GA4 + Microsoft
// Clarity are configured INSIDE GTM (Owner-side), never as direct vendor tags in app code.
//
// DARK / fail-closed: nothing loads unless a real container ID is supplied AND analytics consent
// is granted. Consent Mode v2 (RGPD): a DENIED-by-default consent state is established in the
// dataLayer at init (no network); the GTM script is loaded only on analytics-consent, at which
// point consent is updated to granted (analytics only — never ad storage). Prerender/SSR-safe:
// every browser access is guarded, so the module imports and runs without `window`/`document`.

let consentDefaultSet = false;
let gtmLoaded = false;

function dataLayer() {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

// Minimal gtag shim writing to the dataLayer (GTM reads its history once loaded).
function gtag() {
  const layer = dataLayer();
  if (layer) layer.push(arguments);
}

// Consent Mode v2 — establish the DENIED-by-default state. Safe to call pre-consent (no network).
export function setupConsentModeDefault() {
  if (consentDefaultSet || typeof window === 'undefined') return false;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
  consentDefaultSet = true;
  return true;
}

// Inject the GTM container script (idempotent). DARK: no container id → no-op.
export function loadGtmContainer(containerId) {
  if (gtmLoaded || typeof window === 'undefined' || typeof document === 'undefined') return false;
  const id = typeof containerId === 'string' ? containerId.trim() : '';
  if (!id) return false; // dark / fail-closed
  setupConsentModeDefault();
  const layer = dataLayer();
  if (layer) layer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
  gtmLoaded = true;
  return true;
}

// On analytics consent → load GTM (OPS-4: GTM loads only after consent) + Consent Mode update granted.
export function grantAnalyticsConsentMode(containerId) {
  const loaded = loadGtmContainer(containerId);
  gtag('consent', 'update', { analytics_storage: 'granted' });
  return loaded;
}

// On withdrawal → Consent Mode update denied (analytics storage stops; GTM stays loaded if present).
export function withdrawAnalyticsConsentMode() {
  if (typeof window === 'undefined') return;
  gtag('consent', 'update', { analytics_storage: 'denied' });
}

// test-only reset of the idempotency latches
export function __resetGtmForTests() {
  consentDefaultSet = false;
  gtmLoaded = false;
}
